import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowRight, Gamepad2, Info, X } from 'lucide-react'
import { useAuthStore } from '@/store/auth'
import { useAppStore } from '@/store/app'
import { useVoiceStore } from '@/store/voice'
import { useAchievementsStore } from '@/store/achievements'
import { releasesApi, releaseImageUrl, type Release, type Announcement } from '@/lib/releasesApi'
import { renderMarkdown } from '@/lib/markdown'
import { GAME_SERVERS, GAME_SERVERS_RELEASE_ID, gameServersSeen } from '@/lib/gameServersAnnouncement'
import styles from './Announcements.module.css'

function canShowNow() {
  return document.visibilityState === 'visible' && document.documentElement.dataset.keyboard !== 'true'
}

/** Independent announcements share account receipts with the publication editor. */
export function Announcements({ paused = false }: { paused?: boolean }) {
  const userId = useAuthStore((s) => s.user?.id)
  // Account-keyed child also resets an open card when switching accounts.
  return userId ? <AccountAnnouncement key={userId} userId={userId} paused={paused} /> : null
}

function AccountAnnouncement({ userId, paused }: { userId: string; paused: boolean }) {
  const qc = useQueryClient()
  const queryKey = ['releases', 'announcements', userId] as const
  const pending = useQuery({ queryKey, queryFn: releasesApi.announcements, staleTime: 60_000, refetchInterval: 60_000, refetchOnWindowFocus: 'always' })
  const inVoice = useVoiceStore((s) => s.connection !== null)
  const settings = useAppStore((s) => s.settings)
  const achievement = useAchievementsStore((s) => s.queue.length > 0)
  const [visible, setVisible] = useState(canShowNow)
  const [shown, setShown] = useState<Release | null>(null)
  const claimed = useRef(new Set<string>())
  const blocked = paused || inVoice || settings !== null || achievement || !visible
  const { mutate: recordSeen } = useMutation({
    mutationFn: async ({ id, opened }: { id: string; opened: boolean }) => {
      // A retry must never mark a different signed-in account as having seen this notice.
      if (useAuthStore.getState().user?.id !== userId) return
      await releasesApi.seen(id, opened)
    },
    retry: 2,
    onSuccess: () => { void qc.invalidateQueries({ queryKey }) },
  })

  useEffect(() => {
    const update = () => setVisible(canShowNow())
    const observer = new MutationObserver(update)
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-keyboard'] })
    document.addEventListener('visibilitychange', update)
    return () => { observer.disconnect(); document.removeEventListener('visibilitychange', update) }
  }, [])

  const candidates = pending.data?.releases
  useEffect(() => {
    const release = candidates?.find(r => !claimed.current.has(r.id))
    if (!release || !release.announcement || blocked || shown || claimed.current.has(release.id)) return
    // Defer until the visible frame; cancel if a call, keyboard or modal opens meanwhile.
    const frame = requestAnimationFrame(() => {
      if (!canShowNow() || useAuthStore.getState().user?.id !== userId) return
      claimed.current.add(release.id)
      if (!announcementSeen(userId, release.id)) {
        setShown(release)
        rememberAnnouncement(userId, release.id)
      }
      // Recover a failed receipt on the next visit without showing the card again locally.
      recordSeen({ id: release.id, opened: false })
    })
    return () => cancelAnimationFrame(frame)
  }, [candidates, blocked, userId, recordSeen, shown])

  if (!shown || blocked) return null
  return <AnnouncementCard release={shown} onClose={() => setShown(null)} onExplore={() => {
    recordSeen({ id: shown.id, opened: true })
    setShown(null)
    followAnnouncement(shown.announcement!)
  }} />
}

const seenKey = (userId: string, id: string) => `klichat.announcement.seen.${id}.${userId}`
function announcementSeen(userId: string, id: string) {
  try { return localStorage.getItem(seenKey(userId, id)) === '1' || (id === GAME_SERVERS_RELEASE_ID && gameServersSeen(userId)) } catch { return false }
}
function rememberAnnouncement(userId: string, id: string) {
  try { localStorage.setItem(seenKey(userId, id), '1') } catch { /* Account receipt is stored on the server. */ }
}
function followAnnouncement(a: Announcement) {
  const app = useAppStore.getState()
  if (a.target === 'catalog') app.setView({ kind: 'discovery', openedAt: Date.now() })
  else if (a.target === 'help') app.openSettings('help')
  else if (a.target === 'url' && a.url?.startsWith('https://')) window.open(a.url, '_blank', 'noopener,noreferrer')
}

export function AnnouncementCard({ release, onClose, onExplore, preview = false }: {
  release: Pick<Release, 'title' | 'items' | 'announcement'>; onClose: () => void; onExplore: () => void; preview?: boolean
}) {
  const a = release.announcement
  if (!a) return null
  const games = GAME_SERVERS.filter(game => a.games?.includes(game.key))
  return (
    <aside className={`${styles.notice}${preview ? ` ${styles.preview}` : ''}`} data-style={a.style} aria-label={release.title}>
      <button className={styles.close} type="button" aria-label="Закрыть объявление" onClick={onClose}><X size={18} /></button>
      <div className={styles.kicker}>{a.style === 'promo' ? <Gamepad2 size={16} /> : <Info size={16} />} {a.label || 'Кличат'}</div>
      <h2>{release.title}</h2>
      <div className={styles.content}>
        {release.items.map((item, i) => <div key={i}>
          {item.image ? <img className={styles.banner} src={releaseImageUrl(item.image)} alt="" /> : null}
          <div className={styles.copy}>{renderMarkdown(item.text)}</div>
        </div>)}
      </div>
      {games.length > 0 ? <div className={styles.games}>
        {games.map((game) => <img key={game.key} src={`/game-servers/${game.key}.png?v=2`} alt={game.name} width={38} height={38} />)}
        <span>{games.map(game => game.name).join(' · ')}</span>
      </div> : null}
      {a.target !== 'none' ? <button className={styles.explore} type="button" onClick={onExplore}>{a.button} <ArrowRight size={17} /></button> : null}
    </aside>
  )
}
